import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { HiPencil, HiTrash } from "react-icons/hi2";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import CustomSearchBar from "../../../ui/CustomSearchBar/CustomSearchBar";
import ColumnVisibilitySelector from "../../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import useManualEntryStore from "../../../../stores/manualEntryStore";
import { toastService } from "../../../../providers";
import { formatKES, getDisplayValue } from "../../../../utils/transactionutils";
import { MANUAL_ENTRY_CHANNELS } from "../config/manualEntryConfig";

const remarksColumn = {
  accessorKey: "remarks",
  header: "Remarks",
  cell: (info) => (
    <div className="max-w-[200px] whitespace-normal break-words text-xs text-gray-700 leading-snug py-1">
      {getDisplayValue(info.getValue())}
    </div>
  ),
  enableSorting: false,
  size: 160,
};

const errorsColumn = {
  accessorKey: "errors",
  header: "Errors",
  cell: (info) => {
    const errs = info.getValue();
    if (!errs?.length) return <span className="text-xs text-gray-400">—</span>;
    return (
      <ul className="list-disc list-inside space-y-0.5 max-w-[240px] whitespace-normal break-words leading-snug py-1">
        {errs.map((e, i) => (
          <li key={i} className="text-xs text-red-600">
            {e}
          </li>
        ))}
      </ul>
    );
  },
  enableSorting: false,
  size: 200,
};

const phoneColumn = {
  accessorKey: "phoneNumber",
  header: "Phone",
  cell: (info) => getDisplayValue(info.getValue()),
  enableSorting: false,
};

const accountNumberColumn = {
  accessorKey: "accountNumber",
  header: "Account No.",
  cell: (info) => getDisplayValue(info.getValue()),
  enableSorting: false,
};

function buildColumns({ onEdit, onDelete, hasErrors, isBank }) {
  const identifierColumn = isBank ? accountNumberColumn : phoneColumn;

  return [
    {
      accessorKey: "recipientName",
      header: "Recipient Name",
      cell: (info) => getDisplayValue(info.getValue()),
      enableSorting: false,
    },
    identifierColumn,
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => formatKES(info.getValue()),
      enableSorting: false,
    },
    {
      accessorKey: "charges",
      header: "Charges",
      cell: (info) => formatKES(info.getValue()),
      enableSorting: false,
    },
    ...(hasErrors ? [remarksColumn, errorsColumn] : []),
    {
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex gap-2">
            <button
              title="Edit entry"
              className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(row);
              }}
            >
              <HiPencil size={16} />
            </button>
            <button
              title="Remove entry"
              className="p-1.5 rounded-md text-error-600 hover:bg-error-50  transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(row.id);
              }}
            >
              <HiTrash size={16} />
            </button>
          </div>
        );
      },
      enableSorting: false,
      size: 80,
    },
  ];
}

function ManualEntryTable({ onEdit }) {
  const entries = useManualEntryStore((s) => s.entries);
  const paymentChannel = useManualEntryStore((s) => s.paymentChannel);

  const isBank = paymentChannel === MANUAL_ENTRY_CHANNELS.BANK;

  const [searchQuery, setSearchQuery] = useState("");
  const [visibleOptionalKeys, setVisibleOptionalKeys] = useState([]);

  const hasErrors = entries.some((e) => e.errors?.length > 0);

  const errorDefaultColumnKeys = useMemo(
    () => [
      "recipientName",
      isBank ? "accountNumber" : "phoneNumber",
      "errors",
      "actions",
    ],
    [isBank],
  );

  const errorColumnMeta = useMemo(
    () => [
      { key: "recipientName", label: "Recipient Name" },
      isBank
        ? { key: "accountNumber", label: "Account No." }
        : { key: "phoneNumber", label: "Phone" },
      { key: "errors", label: "Errors" },
      { key: "actions", label: "Actions" },
      { key: "amount", label: "Amount" },
      { key: "charges", label: "Charges" },
      { key: "remarks", label: "Remarks" },
    ],
    [isBank],
  );

  const columnVisibility = useMemo(() => {
    if (!hasErrors) return undefined;
    return {
      amount: visibleOptionalKeys.includes("amount"),
      charges: visibleOptionalKeys.includes("charges"),
      remarks: visibleOptionalKeys.includes("remarks"),
    };
  }, [hasErrors, visibleOptionalKeys]);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const matchesName = e.recipientName?.toLowerCase().includes(q);
      const matchesIdentifier = isBank
        ? e.accountNumber?.toLowerCase().includes(q)
        : e.phoneNumber?.toLowerCase().includes(q);
      return matchesName || matchesIdentifier;
    });
  }, [entries, searchQuery, isBank]);

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit,
        onDelete: (id) => {
          useManualEntryStore.getState().removeEntry(id);
          toastService.info("Entry removed from batch.");
        },
        hasErrors,
        isBank,
      }),
    [onEdit, hasErrors, isBank],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <CustomSearchBar
          placeholder={
            isBank
              ? "Search by name or account number"
              : "Search by name or phone number"
          }
          onSearchChange={setSearchQuery}
          debounceDelay={500}
          size="sm"
          radius="md"
          style={{ width: 220 }}
        />
        {hasErrors && (
          <ColumnVisibilitySelector
            columns={errorColumnMeta}
            defaultColumnKeys={errorDefaultColumnKeys}
            visibleColumnKeys={visibleOptionalKeys}
            onChange={setVisibleOptionalKeys}
          />
        )}
      </div>
      <BaseTable
        data={filteredEntries}
        columns={columns}
        columnVisibility={columnVisibility}
        paginationMode="client"
        pageSize={10}
        emptyMessage={
          searchQuery
            ? "No recipients match your search."
            : "No recipients added yet. Fill in the form above to add entries."
        }
        rowClassName={() => "bg-white-50 min-h-[50px]"}
      />
    </div>
  );
}

ManualEntryTable.propTypes = {
  onEdit: PropTypes.func,
};

export default ManualEntryTable;
